import { z } from 'zod';
export const ExcelDateFormatSchema = z.enum(['YYYYMMDD', 'YYYY-MM-DD', 'YYYY-MM']);
export const ExcelColumnTypeSchema = z.enum(['text', 'numeric', 'timestamptz', 'boolean']);
export const ExcelDetectedTypeSchema = z.enum([
    'number',
    'date',
    'boolean',
    'string',
    'mixed',
    'empty',
]);
export const ExcelAppendStrategySchema = z.enum(['pk-upsert', 'date-replace', 'append']);
/** 엑셀 파싱 결과의 한 컬럼 (사용자 검토용). */
export const ExcelParseSheetColumnSchema = z.object({
    /** sanitize 된 SQL 컬럼명 (e.g. 'col_1', 'order_no'). */
    sqlName: z.string(),
    /** 원본 헤더 텍스트. null 이면 헤더가 없거나 빈 칸 — 사용자 입력 필수. */
    rawHeader: z.string().nullable(),
    detectedType: ExcelDetectedTypeSchema,
    sampleValues: z.array(z.unknown()).max(20),
    nullCount: z.number().int().nonnegative(),
    distinctCount: z.number().int().nullable(),
});
/** 시트 상단 narrative (제목·머리말·병합 라벨·셀 메모) — 어떤 데이터인지 LLM 이 추론할 단서. */
export const ExcelParseSheetNarrativeSchema = z.object({
    /** 헤더 행 위쪽 (제목·작성자·단위 안내 등). */
    preHeader: z.array(z.string()).max(20),
    /** 병합 셀 라벨 — "A1:D1 → 매출 요약" 형태. */
    merged: z.array(z.string()).max(20),
    /** 셀 메모/코멘트. */
    notes: z.array(z.string()).max(20),
});
export const ExcelParseSheetSchema = z.object({
    sheetName: z.string(),
    headerRowIndex: z.number().int().nullable(),
    rowCount: z.number().int().nonnegative(),
    /** narrative-only 시트 (제목/안내문 등) false. UI 에서 자동 비활성화. */
    isData: z.boolean(),
    columns: z.array(ExcelParseSheetColumnSchema),
    head: z.array(z.record(z.string(), z.unknown())).max(50),
    /** 시트 상단의 제목/메모/병합 라벨. LLM 이 시트 의미를 추론하는 데 사용. 없으면 빈 배열. */
    narrative: ExcelParseSheetNarrativeSchema.default({ preHeader: [], merged: [], notes: [] }),
});
export const ExcelParseResultSchema = z.object({
    fileUploadId: z.string().uuid(),
    excelDatasetId: z.string().uuid(),
    sheets: z.array(ExcelParseSheetSchema),
});
/**
 * 한 시트의 컬럼 commit 본 — 영구 SQL 컬럼명 (한글 가능) + 임시 테이블 매핑.
 *
 * sqlName/label 은 max 만 검증한다. 비활성(`include=false`) 컬럼은 빈 값을 허용해야 하고
 * (자동 제외된 빈 헤더 컬럼은 사용자가 라벨을 안 채워도 통과해야 함), 활성 컬럼의 비어있음 검증은
 * 시트 레벨 superRefine 에서 수행.
 */
export const ExcelColumnCommitSchema = z.object({
    /** 영구 SQL 컬럼명. PostgreSQL quote_ident 가 한글/공백을 지원하므로 자유. 사용자가 편집 가능. */
    sqlName: z.string().max(63),
    /**
     * 임시 테이블 (excel_uploads 스키마) 의 SQL 컬럼명.
     * buildSheetSummary 결과의 영문 sqlName 그대로. INSERT INTO permanent SELECT FROM temp 매핑용.
     */
    parseSqlName: z.string().min(1).max(63),
    /** 사람이 읽는 라벨 (보통 원본 헤더). dataSourceColumns.description 으로 저장. */
    label: z.string().max(200),
    description: z.string().max(2000).nullable(),
    type: ExcelColumnTypeSchema,
    /** false 면 이 컬럼은 영구 테이블에 만들지 않음. 빈 컬럼 등 제거용. */
    include: z.boolean().default(true),
});
/** 신규 데이터소스 생성 — 한 시트의 사용자 입력 본. */
export const ExcelSheetCommitSchema = z
    .object({
    sheetName: z.string(),
    /** false 면 이 시트 skip. narrative-only 자동 false. */
    include: z.boolean(),
    /** SQL 테이블명. 한글 가능 (PostgreSQL quote_ident). 사용자 편집 가능. */
    tableName: z.string().min(1).max(63),
    columns: z.array(ExcelColumnCommitSchema).min(1),
    pkColumns: z.array(z.string()).default([]),
    dateColumn: z.string().nullable(),
    dateFormat: ExcelDateFormatSchema.nullable(),
})
    .superRefine((s, ctx) => {
    if (!s.include)
        return; // 시트 자체가 비활성이면 컬럼 검증 skip.
    const included = s.columns.filter((c) => c.include !== false);
    if (included.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: `포함된 컬럼이 없습니다 (시트 "${s.sheetName}"): 한 컬럼 이상 체크하세요.`,
            path: ['columns'],
        });
        return;
    }
    for (let i = 0; i < s.columns.length; i++) {
        const c = s.columns[i];
        if (!c || c.include === false)
            continue;
        if (c.sqlName.trim().length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: `SQL 컬럼명이 비었습니다 (시트 "${s.sheetName}", 컬럼 #${i + 1})`,
                path: ['columns', i, 'sqlName'],
            });
        }
        if (c.label.trim().length === 0) {
            ctx.addIssue({
                code: 'custom',
                message: `한글 라벨이 비었습니다 (시트 "${s.sheetName}", 컬럼 #${i + 1})`,
                path: ['columns', i, 'label'],
            });
        }
    }
});
// ----- PK 중복 검사 (등록 전 사전 검증) -----
export const ExcelCheckPkDuplicatesRequestSchema = z.object({
    fileUploadId: z.string().uuid(),
    sheetName: z.string().min(1),
    /** 임시 테이블의 SQL 컬럼명 (parseSqlName 들). */
    pkParseColumns: z.array(z.string().min(1)).min(1).max(8),
});
export const ExcelCheckPkDuplicatesResponseSchema = z.object({
    hasDuplicates: z.boolean(),
    /** 중복 그룹 수 (HAVING count(*)>1 행 수). 클 수 있음. */
    totalDuplicateGroups: z.number().int().nonnegative(),
    /** 샘플 — PK 값 + 중복 횟수. 최대 10 개. */
    samples: z
        .array(z.object({
        values: z.array(z.string().nullable()),
        count: z.number().int().positive(),
    }))
        .max(10),
});
/** 데이터소스 등록과 함께 스킬도 자동 생성하는 옵션. */
export const CreateExcelDataSourceSkillOptionSchema = z.object({
    slug: z
        .string()
        .regex(/^[a-z0-9-]+$/, 'slug 은 소문자 영문/숫자/하이픈만 허용합니다.')
        .min(2)
        .max(40),
    name: z.string().min(1).max(80),
    description: z.string().max(2000).nullable().default(null),
});
export const CreateExcelDataSourceFromUploadRequestSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(2000).nullable(),
    fileUploadId: z.string().uuid(),
    sheets: z.array(ExcelSheetCommitSchema).min(1),
    /** 함께 만들 스킬 옵션. null = 스킬 안 만듦. */
    createSkill: CreateExcelDataSourceSkillOptionSchema.nullable().default(null),
});
/** 추가 업로드 — 매핑 정보. 시트명 동일 시 자동 매핑되므로 사용자가 명시한 것만 전달. */
export const ExcelSheetAppendMappingSchema = z.object({
    newSheetName: z.string(),
    /** null = 무시 (skip). 신규 테이블 생성 시 createNewTable=true 와 newTableConfig. */
    targetTableId: z.string().uuid().nullable(),
    createNewTable: z.boolean().default(false),
    newTableConfig: ExcelSheetCommitSchema.nullable(),
});
export const AppendExcelMappingRequestSchema = z.object({
    fileUploadId: z.string().uuid(),
    mappings: z.array(ExcelSheetAppendMappingSchema),
});
/** 추가 업로드 parse 응답에서, 시트별 자동 매칭 결과. */
export const ExcelAutoMappingItemSchema = z.object({
    newSheetName: z.string(),
    targetTableId: z.string().uuid().nullable(),
    matchKind: z.enum(['exact', 'none']),
});
export const AppendExcelParseResultSchema = ExcelParseResultSchema.extend({
    autoMapping: z.array(ExcelAutoMappingItemSchema),
});
/** SSE 적재 결과 — 시트별 행수 / 에러. */
export const ExcelIngestSheetResultSchema = z.object({
    sheetName: z.string(),
    targetTableId: z.string().uuid().nullable(),
    rowsInserted: z.number().int().nonnegative(),
    rowsUpdated: z.number().int().nonnegative().default(0),
    rowsDeleted: z.number().int().nonnegative().default(0),
    error: z.string().nullable(),
});
export const ExcelDataSourceMetaSchema = z.object({
    dataSourceTableId: z.string().uuid(),
    sheetName: z.string(),
    permanentSchema: z.string(),
    permanentTableName: z.string(),
    pkColumns: z.array(z.string()),
    dateColumn: z.string().nullable(),
    dateFormat: ExcelDateFormatSchema.nullable(),
    appendStrategy: ExcelAppendStrategySchema,
    lastIngestRowCount: z.number().int().nullable(),
    lastIngestAt: z.string().nullable(),
});
/** SSE 이벤트 페이로드 (frontend 의 excelDataSourceStream 가 emit). */
export const ExcelStreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('progress'), message: z.string() }),
    z.object({ type: z.literal('sheet_start'), sheetName: z.string() }),
    z.object({
        type: z.literal('sheet_done'),
        sheetName: z.string(),
        rowCount: z.number().int().nonnegative(),
        isData: z.boolean(),
    }),
    z.object({ type: z.literal('heartbeat'), elapsedMs: z.number().int().nonnegative() }),
    z.object({ type: z.literal('error'), message: z.string() }),
    z.object({ type: z.literal('parse_done'), result: ExcelParseResultSchema }),
    z.object({ type: z.literal('append_parse_done'), result: AppendExcelParseResultSchema }),
    z.object({
        type: z.literal('ingest_sheet_done'),
        result: ExcelIngestSheetResultSchema,
    }),
    z.object({
        type: z.literal('done'),
        dataSourceId: z.string().uuid().nullable(),
        sheets: z.array(ExcelIngestSheetResultSchema).default([]),
        /** 동시에 생성된 스킬의 id. createSkill 옵션이 없었거나 데이터소스 생성이 아닌 흐름이면 null. */
        skillId: z.string().uuid().nullable().default(null),
    }),
]);
//# sourceMappingURL=data-source-excel.js.map